// A tool taking the room beside My Team's dock (PLAN-2026-10-17 §2.3 and §3,
// B-400): the whole screen the tool has of its own — Players with every
// column, Waivers with the claims and the order, Trades with the offers and
// the builder — drawn in the pane rather than in place of the page.
//
// IT LAYS ITSELF OUT BY THE ROOM IT IS GIVEN. The pane is measured, and the
// screen inside is told the pane's tier (`TierScope`), so Players in a pane
// 900 px wide on a 1280 window draws its columns for 900, and on a tablet the
// tool draws its one-column arrangement, as it would on a phone.

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { watchPhoneFlow } from '../../kit/phoneFlow';
import { TierScope, tierFor, useWindowTier, type Tier } from '../../shell/useTier';
import { NewsScreen } from '../NewsScreen';
import { PlanScreen } from '../PlanScreen';
import { PlayersScreen } from '../PlayersScreen';
import { TradesScreen } from '../TradesScreen';
import { WaiversScreen } from '../WaiversScreen';
import type { SideTool } from './TeamSide';

/** Each tool's own screen, as its own address draws it. */
export function ToolScreen({ tool }: { tool: SideTool }): ReactNode {
  switch (tool) {
    case 'players':
      return <PlayersScreen />;
    case 'waivers':
      return <WaiversScreen />;
    case 'trades':
      return <TradesScreen />;
    case 'plan':
      return <PlanScreen />;
    case 'news':
      return <NewsScreen />;
  }
}

/**
 * The pane's tier before it has been measured: the window's less the rail and
 * the dock, so the first frame is drawn for about the right width.
 */
function guess(window: Tier): Tier {
  return window === 'large' ? 'desktop' : window === 'desktop' ? 'tablet' : 'phone';
}

export function ToolRoom({ tool }: { tool: SideTool }) {
  const box = useRef<HTMLDivElement>(null);
  const windowTier = useWindowTier();
  const [tier, setTier] = useState<Tier>(() => guess(windowTier));
  useLayoutEffect(() => {
    const el = box.current;
    if (el === null) return;
    const measure = () => setTier(tierFor(el.getBoundingClientRect().width));
    measure();
    const sizes = new ResizeObserver(measure);
    sizes.observe(el);
    return () => sizes.disconnect();
  }, []);
  // AT A PHONE'S WIDTH THE SCREEN SCROLLS AS ONE (UX-38), and its sticky row's
  // height is measured for the column names that stick under it, as the
  // frame does for a phone's whole screen.
  useLayoutEffect(() => {
    if (tier !== 'phone' || box.current === null) return;
    return watchPhoneFlow(box.current);
  }, [tier]);
  return (
    <div className="fsp-tool-room" ref={box}>
      <TierScope tier={tier}>
        <ToolScreen tool={tool} />
      </TierScope>
    </div>
  );
}
